import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowUp, Banknote, ChevronRight, Heart, Home, ListChecks, LogOut, Menu, MoreHorizontal, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

let notificationAudioContext = null;

export default function AppShell({ title, subtitle, nav = [], children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const desktopNavRef = useRef(null);
  const restoringNavRef = useRef(false);
  const notificationBaselineRef = useRef(false);

  useEffect(() => {
    if (!user || user === false) return;
    let active = true;
    notificationBaselineRef.current = false;
    const refreshNotifications = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!active) return;
        setNotifications((data.items || []).filter((i) => Number(i.count || 0) > 0));
        const isInitialFetch = !notificationBaselineRef.current;
        for (const event of (data.events || [])) {
          if (!event?.key || !event?.id) continue;
          const storageKey = `kaya:last-notification:${user.role}:${event.key}`;
          const previousId = localStorage.getItem(storageKey);
          if (!isInitialFetch && previousId !== event.id) {
            toast.info(event.message || "New activity received");
            playNotificationSound();
          }
          localStorage.setItem(storageKey, event.id);
        }
        notificationBaselineRef.current = true;
      } catch (_) {}
    };
    refreshNotifications();
    const timer = window.setInterval(refreshNotifications, 10000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [user]);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const isNavActive = (item) => {
    if (item.section) return false; // scroll links are never "active" in URL
    return loc.pathname === item.to || (item.to !== "/employee" && item.to !== "/admin" && loc.pathname.startsWith(item.to));
  };

  const navScrollKey = `kaya:nav-scroll:${user?.role || "unknown"}`;

  useLayoutEffect(() => {
    const navElement = desktopNavRef.current;
    if (!navElement) return;
    const savedPosition = Number(sessionStorage.getItem(navScrollKey) || 0);
    restoringNavRef.current = true;
    navElement.scrollTop = savedPosition;
    const frame = window.requestAnimationFrame(() => {
      navElement.scrollTop = savedPosition;
      restoringNavRef.current = false;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [navScrollKey]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [loc.pathname]);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 500);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const activeItem = nav.find(isNavActive);
  const pageTitle = activeItem && activeItem.to !== "/admin" && activeItem.to !== "/employee" ? activeItem.label : title;

  const handleNavClick = (item, e) => {
    if (desktopNavRef.current) {
      sessionStorage.setItem(navScrollKey, String(desktopNavRef.current.scrollTop));
    }
    setMobileOpen(false);
    if (item.section) {
      e.preventDefault();
      const el = document.getElementById(item.section);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderNavLinks = ({ onClick, navRef }) => (
    <nav ref={navRef} onScroll={navRef ? (event) => {
      if (!restoringNavRef.current) sessionStorage.setItem(navScrollKey, String(event.currentTarget.scrollTop));
    } : undefined} className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-1">
      {nav.map((item, index) => {
        const isActive = isNavActive(item);
        const Icon = item.icon;
        const commonClass = `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
          isActive ? "bg-[#4A5D4E] text-white shadow-sm" : "text-[#5C635E] hover:bg-white/70"
        }`;
        const showGroup = item.group && item.group !== nav[index - 1]?.group;
        return (
          <React.Fragment key={item.key}>
          {showGroup && <div className={`${index ? "pt-4" : ""} px-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A938D]`}>{item.group}</div>}
          <Link
            to={item.to}
            data-testid={`nav-${item.key}`}
            aria-current={isActive ? "page" : undefined}
            className={commonClass}
            onClick={(e) => { handleNavClick(item, e); onClick && onClick(); }}
          >
            {Icon && <Icon className="h-4 w-4" />}
            <span>{item.label}</span>
          </Link>
          </React.Fragment>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-[#EAE6DF] bg-[#F4F1EA]/60 p-6 sticky top-0 h-screen shrink-0 overflow-hidden">
        <div className="flex items-center gap-3 mb-10 shrink-0">
          <div className="h-10 w-10 rounded-2xl bg-[#4A5D4E] flex items-center justify-center">
            <Heart className="h-5 w-5 text-white" fill="currentColor" />
          </div>
          <div>
            <div className="font-semibold text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Kaya Solutions</div>
            <div className="text-[10px] tracking-widest uppercase text-[#8A938D]">Donation Management</div>
          </div>
        </div>

        <div className="text-overline mb-3 shrink-0">Navigate</div>
        {renderNavLinks({ navRef: desktopNavRef })}

        <div className="mt-6 pt-6 border-t border-[#EAE6DF] shrink-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-9 w-9 rounded-full bg-[#4A5D4E]/10 flex items-center justify-center text-[#4A5D4E] font-semibold">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#2D312E] truncate">{user?.name}</div>
              <div className="text-xs text-[#8A938D] capitalize">{user?.role}</div>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={doLogout}
            className="w-full flex items-center justify-center gap-2 border border-[#EAE6DF] hover:border-[#C05746] hover:text-[#C05746] text-[#5C635E] rounded-xl px-4 py-2.5 text-sm transition-all"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-nav-backdrop"
          />
          <aside className="fixed top-0 left-0 bottom-0 w-72 bg-[#F4F1EA] z-50 md:hidden flex flex-col p-6 shadow-2xl overflow-hidden" data-testid="mobile-nav-drawer">
            <div className="flex items-center justify-between mb-8 shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl bg-[#4A5D4E] flex items-center justify-center">
                  <Heart className="h-5 w-5 text-white" fill="currentColor" />
                </div>
                <div>
                  <div className="font-semibold text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Kaya Solutions</div>
                  <div className="text-[10px] tracking-widest uppercase text-[#8A938D]">Donation Management</div>
                </div>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                data-testid="close-mobile-nav"
                className="text-[#5C635E]"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="text-overline mb-3 shrink-0">Navigate</div>
            {renderNavLinks({ onClick: () => setMobileOpen(false) })}
            <div className="mt-6 pt-6 border-t border-[#EAE6DF] shrink-0">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-9 w-9 rounded-full bg-[#4A5D4E]/10 flex items-center justify-center text-[#4A5D4E] font-semibold">
                  {(user?.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#2D312E] truncate">{user?.name}</div>
                  <div className="text-xs text-[#8A938D] capitalize">{user?.role}</div>
                </div>
              </div>
              <button
                data-testid="logout-button-drawer"
                onClick={doLogout}
                className="w-full flex items-center justify-center gap-2 border border-[#EAE6DF] hover:border-[#C05746] hover:text-[#C05746] text-[#5C635E] rounded-xl px-4 py-2.5 text-sm transition-all"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main */}
      <main id="main-content" className="flex-1 min-w-0" tabIndex="-1">
        {/* mobile top bar */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-[#EAE6DF] bg-white sticky top-0 z-30">
          <button
            data-testid="open-mobile-nav"
            onClick={() => setMobileOpen(true)}
            className="text-[#5C635E] p-1"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-[#4A5D4E] flex items-center justify-center">
              <Heart className="h-4 w-4 text-white" fill="currentColor" />
            </div>
            <span className="font-semibold text-sm" style={{ fontFamily: "Manrope" }}>Kaya Solutions</span>
          </div>
          <button
            data-testid="logout-button-mobile"
            onClick={doLogout}
            className="text-[#5C635E] p-1"
            aria-label="Sign out"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>

        <div className={`p-4 sm:p-6 md:p-10 max-w-7xl mx-auto ${user?.role === "employee" ? "pb-24 md:pb-10" : ""}`}>
          {title && (
            <div className="mb-6 md:mb-8">
              {pageTitle !== title && <div className="flex items-center gap-1 text-xs text-[#8A938D] mb-2"><span>{title}</span><ChevronRight className="h-3 w-3" /><span className="text-[#4A5D4E]">{pageTitle}</span></div>}
              <h1 className="text-3xl sm:text-4xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>{pageTitle}</h1>
              {subtitle && <p className="text-[#5C635E] mt-1">{subtitle}</p>}
              {notifications.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4" data-testid="notifications-strip">
                  {notifications.map((n) => (
                    <span key={n.key} className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-800">
                      {n.label}: <b>{n.count}</b>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {children}
        </div>
      </main>
      {user?.role === "employee" && <nav aria-label="Primary mobile navigation" className="md:hidden fixed bottom-0 inset-x-0 z-30 grid grid-cols-4 border-t border-[#EAE6DF] bg-white/95 backdrop-blur px-2 pb-[max(.5rem,env(safe-area-inset-bottom))] pt-2 shadow-[0_-8px_24px_rgba(45,49,46,.08)]">
        {[
          ["Overview", "/employee", Home],
          ["Donations", "/employee/donations", ListChecks],
          ["Payroll", "/employee/payroll", Banknote],
        ].map(([label, to, Icon]) => { const active = loc.pathname === to; return <Link key={to} to={to} aria-current={active ? "page" : undefined} className={`min-h-12 rounded-xl flex flex-col items-center justify-center gap-1 text-[10px] ${active ? "text-[#4A5D4E] bg-[#4A5D4E]/10 font-semibold" : "text-[#6F7771]"}`}><Icon className="h-5 w-5" /><span>{label}</span></Link>; })}
        <button type="button" onClick={() => setMobileOpen(true)} className="min-h-12 rounded-xl flex flex-col items-center justify-center gap-1 text-[10px] text-[#6F7771]" aria-label="More navigation"><MoreHorizontal className="h-5 w-5" /><span>More</span></button>
      </nav>}
      {showBackToTop && <button type="button" aria-label="Back to top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className={`fixed right-5 z-30 h-11 w-11 rounded-full bg-[#4A5D4E] text-white shadow-lg flex items-center justify-center hover:bg-[#3B4A3E] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4A5D4E] ${user?.role === "employee" ? "bottom-20 md:bottom-5" : "bottom-5"}`}><ArrowUp className="h-5 w-5" /></button>}
    </div>
  );
}

async function playNotificationSound() {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    if (!notificationAudioContext || notificationAudioContext.state === "closed") {
      notificationAudioContext = new AudioContextClass();
    }
    const context = notificationAudioContext;
    if (context.state === "suspended") await context.resume();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(1250, context.currentTime);
    oscillator.frequency.setValueAtTime(1550, context.currentTime + 0.08);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.38, context.currentTime + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.3);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.32);
  } catch (_) {
    // Browser autoplay policies may block sound until the user interacts.
  }
}
